import React, { useEffect, useMemo, useState } from 'react';
import ChatAvatarComponent from '../../../chat/ChatAvatarComponent';
import { Dialog, Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import { navigateToChat } from '../../../NavigateToChat';
import { fetchUserWorkHistory } from '../../../../../../redux/slices/user-work/userWorkHistorySlice';
import { fetchUserEducation } from '../../../../../../redux/slices/user-education/userEducationSlice';
import { useMemberWord } from '../../../../../../utils/memberTypeLabels';

interface MenteeProfileModalProps {
  open: boolean;
  onClose: () => void;
  user: any;
  objectivesTitleOverride?: string;
  matcheeUser?: any;
  candidateLabel?: string;
  matcheeLabel?: string;
}

/** Returns true if value has meaningful content (non-empty string, non-empty array, or object with at least one non-empty value) */
function hasValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0 && value.some((v) => hasValue(v));
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return Object.keys(obj).length > 0 && Object.values(obj).some((v) => hasValue(v));
  }
  return true;
}

const MenteeProfileModal: React.FC<MenteeProfileModalProps> = ({
  open,
  onClose,
  user: userProp,
  objectivesTitleOverride,
  matcheeUser,
  candidateLabel = 'Match Profile',
  matcheeLabel = 'Your Profile'
}) => {
  const [activeTab, setActiveTab] = useState<'candidate' | 'matchee'>('candidate');

  useEffect(() => {
    if (open) setActiveTab('candidate');
  }, [open]);

  const displayUser = activeTab === 'matchee' && matcheeUser ? matcheeUser : userProp;
  const user = displayUser?.userDetails ?? displayUser;
  const toText = (value: unknown, fallback: string = 'Not set'): string => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (Array.isArray(value)) {
      const flattened: string[] = value.map((item) => toText(item, '')).filter(Boolean);
      return flattened.length ? flattened.join(' · ') : fallback;
    }
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      const candidate = obj.name || obj.title || obj.label || obj.value || obj.id;
      return typeof candidate === 'string' || typeof candidate === 'number' ? String(candidate) : fallback;
    }
    return fallback;
  };
  const p = user?.personal_details || {};
  const extended = user?.extended || {};
  const profileImage =
    p.headshot ||
    p.image ||
    user?.headshot ||
    user?.image ||
    user?.photo ||
    user?.picture ||
    user?.profileImage ||
    user?.profile_image ||
    extended?.headshot ||
    extended?.image ||
    extended?.photo ||
    extended?.picture ||
    undefined;
  const companyInfo = extended.companyInfo || {};
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const communityMembers = useAppSelector((state) => state.acsCommunityUsers.data);
  const personalThreadsArr = useAppSelector((state) => state.acsChannels.personalThreadsArr);
  const workHistoryState = useAppSelector((state) => state.userWorkHistory);
  const educationState = useAppSelector((state) => state.userEducation);
  const groupRoleList = useAppSelector((state) => state.groupRoleList?.data) || [];
  const allGroups = useAppSelector((state) => state.allGroups?.data?.groups) || [];
  const w = useMemberWord();

  const handleMessage = () => {
    const communicationId =
      user?.communicationId ||
      user?.communication_id ||
      user?.commId ||
      user?.communicationID ||
      p?.communicationId ||
      user?.personal_details?.communicationId;
    if (!communicationId) return;
    const listedInCommunity = communityMembers?.find((item) => item?.id?.communicationId === communicationId);
    const isInCommunity = Boolean(listedInCommunity);
    navigateToChat(communicationId, isInCommunity, personalThreadsArr, navigate, dispatch);
    onClose();
  };

  const normalizeList = (input: any): string[] => {
    if (!input) return [];
    if (Array.isArray(input)) return input.filter(Boolean).map(String);
    if (typeof input === 'object') {
      return Object.entries(input)
        .filter(([, value]) => Boolean(value))
        .map(([key]) => key);
    }
    return [String(input)];
  };

  const programs: string[] = useMemo(() => {
    const groupIds = normalizeList(
      user?.groupsAll ||
        user?.allGroupsUserBelongsTo ||
        userProp?.allGroupsUserBelongsTo ||
        user?.programs ||
        user?.groups ||
        user?.groupNames ||
        user?.groupList
    )
      .map((item) => toText(item, '').trim())
      .filter(Boolean);

    if (!groupIds.length) return [];

    const nameById = new Map<string, string>();
    (groupRoleList || []).forEach((group: any) => {
      if (group?.id && group?.name) {
        nameById.set(String(group.id), String(group.name));
      }
    });
    (allGroups || []).forEach((group: any) => {
      if (group?.grpId && group?.name) {
        nameById.set(String(group.grpId), String(group.name));
      }
    });

    return groupIds.map((id) => nameById.get(String(id)) || id);
  }, [user, userProp, groupRoleList, allGroups]);

  const name = toText(user?.name, `${w('mentee')} Name`);
  const resolvedRole = (() => {
    const roles = Array.isArray(user?.role) ? user.role : user?.role ? [user.role] : [];
    let roleStrings = roles.map((r: any) => String(r).toLowerCase());
    const lastseenRole = user?.lastseen?.role ? [String(user.lastseen.role).toLowerCase()] : [];
    const roleInGroupRole = userProp?.roleInGroup?.role ? [String(userProp.roleInGroup.role).toLowerCase()] : [];
    roleStrings = [...roleStrings, ...lastseenRole, ...roleInGroupRole];
    if (roleStrings.includes('mentor')) return 'Mentor';
    if (roleStrings.includes('mentee')) return 'Mentee';
    const typeList = Array.isArray(user?.userType) ? user.userType : user?.userType ? [user.userType] : [];
    const typeStrings = typeList.map((r: any) => String(r).toLowerCase());
    if (typeStrings.includes('mentor')) return 'Mentor';
    if (typeStrings.includes('mentee')) return 'Mentee';
    return 'Member';
  })();
  const resolvedRoleLabel =
    resolvedRole === 'Mentor' ? w('mentor') : resolvedRole === 'Mentee' ? w('mentee') : resolvedRole;
  const roleCategory = toText(p.category, 'Employee');
  const email = toText(user?.email, '');
  const bioText = (() => {
    const sources = [user?.bio, p?.bio, user?.personal_details?.bio, extended?.bio, userProp?.bio].filter(
      (v) => v != null
    );
    for (const raw of sources) {
      if (typeof raw === 'string' && raw.trim()) return raw.trim();
      if (Array.isArray(raw)) {
        const parts = raw.map((item) => (typeof item === 'string' ? item.trim() : toText(item, ''))).filter(Boolean);
        if (parts.length) return parts.join('\n');
      }
      if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
        const str = toText(raw, '');
        if (str && str !== 'Not set') return str;
      }
    }
    return '';
  })();

  const learningObjectives: string[] = (() => {
    const raw =
      user?.extended?.learningObjectives || user?.learningObjectives || user?.learning_objectives || user?.objectives;
    if (Array.isArray(raw)) {
      return raw.map((obj: any) => toText(obj?.objective || obj?.name || obj, '')).filter(Boolean);
    }
    return normalizeList(raw)
      .map((obj) => toText(obj, '').trim())
      .filter(Boolean);
  })();

  const isMatcheeTab = activeTab === 'matchee' && Boolean(matcheeUser);

  const workHistoryList = isMatcheeTab
    ? Array.isArray(user?.professional_details?.workHistory)
      ? user.professional_details.workHistory
      : Array.isArray(user?.workHistory)
      ? user.workHistory
      : []
    : Array.isArray(workHistoryState?.updatedData) && workHistoryState.updatedData.length
    ? workHistoryState.updatedData
    : Array.isArray(user?.professional_details?.workHistory)
    ? user.professional_details.workHistory
    : Array.isArray(user?.workHistory?.updatedData)
    ? user.workHistory.updatedData
    : Array.isArray(user?.workHistory)
    ? user.workHistory
    : [];

  const educationList = isMatcheeTab
    ? Array.isArray(user?.professional_details?.education)
      ? user.professional_details.education
      : Array.isArray(user?.education)
      ? user.education
      : []
    : Array.isArray(educationState?.updatedData) && educationState.updatedData.length
    ? educationState.updatedData
    : Array.isArray(user?.professional_details?.education)
    ? user.professional_details.education
    : Array.isArray(user?.education?.updatedData)
    ? user.education.updatedData
    : Array.isArray(user?.education)
    ? user.education
    : [];

  // Extended company: userDetails.extended.company, companyInfo, companyPositions
  const companyData = useMemo(() => {
    const co = extended.company || user?.company || {};
    const info = extended.companyInfo || {};
    const positions = Array.isArray(extended.companyPositions) ? extended.companyPositions : [];
    const name = toText(co.companyName || co.name || user?.company?.name, '');
    const location = toText(co.companyLocation || co.location || user?.company?.location, '');
    const mission = toText(info.missionStatement, '');
    const fundingStage = toText(info.fundingStage, '');
    const markets = Array.isArray(info.markets) ? info.markets.filter(Boolean).map(String) : [];
    const companySize = toText(info.companySize, '');
    const businessModel = Array.isArray(info.businessModel) ? info.businessModel.filter(Boolean).map(String) : [];
    const customers = toText(info.customers, '');
    const hiring = info.hiring;
    return {
      name,
      location,
      mission,
      fundingStage,
      markets,
      companySize,
      businessModel,
      customers,
      hiring,
      positions
    };
  }, [extended, user]);

  const hasCompanyData =
    hasValue(companyData.name) ||
    hasValue(companyData.location) ||
    hasValue(companyData.mission) ||
    hasValue(companyData.fundingStage) ||
    companyData.markets.length > 0 ||
    hasValue(companyData.companySize) ||
    companyData.businessModel.length > 0 ||
    hasValue(companyData.customers) ||
    companyData.positions.length > 0;

  // Socials: personal_details.social
  const socialsData = useMemo(() => {
    const raw = p.social || user?.social || {};
    const entries: { label: string; url: string }[] = [];
    const map: Record<string, string> = {
      linkedIn: 'LinkedIn',
      facebook: 'Facebook',
      instagram: 'Instagram',
      youtube: 'YouTube',
      twitter: 'X (Twitter)'
    };
    Object.entries(map).forEach(([key, label]) => {
      const url = raw[key];
      if (typeof url === 'string' && url.trim()) entries.push({ label, url: url.trim() });
    });
    return entries;
  }, [p.social, user?.social]);

  const hasSocialsData = socialsData.length > 0;

  // Basic personal details (timezone, country, etc.) — show every field that's filled.
  const phonePretty = (() => {
    const num = toText(p.phoneNumber, '');
    if (!num) return '';
    const prefix = toText(p.phonePrefix, '');
    return prefix ? `${prefix} ${num}`.trim() : num;
  })();
  const detailRows: { label: string; value: string }[] = [
    { label: 'Timezone', value: toText(p.timezone, '') },
    { label: 'Country', value: toText(p.country, '') },
    { label: 'Phone', value: phonePretty },
    { label: 'Gender', value: toText(p.gender, '') },
    { label: 'Date of birth', value: toText(p.dob, '') },
    { label: 'Ethnicity', value: toText(p.ethnicity, '') },
    { label: 'First-generation student', value: toText(p.firstGenStudent, '') }
  ].filter((r) => hasValue(r.value));

  const isObjectId = (s: string) => /^[a-f0-9]{24}$/i.test(s);
  const departmentNames: string[] = (() => {
    const raw = user?.departments;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((d: any) => (d && typeof d === 'object' ? toText(d.name || d.value, '') : String(d)))
      .filter(Boolean)
      .filter((s: string) => !isObjectId(s));
  })();
  const hobbies: string[] = Array.isArray(p.hobbies) ? p.hobbies.filter(Boolean).map(String) : [];
  const disability: string[] = Array.isArray(p.disability) ? p.disability.filter(Boolean).map(String) : [];

  const hasDetails = detailRows.length > 0 || departmentNames.length > 0 || hobbies.length > 0 || disability.length > 0;

  useEffect(() => {
    if (!open) return;
    if (activeTab === 'matchee' && matcheeUser) return;
    const targetUserId = user?.id || user?._id || user?.personal_details?.coId || '';
    if (!targetUserId) return;
    dispatch(fetchUserWorkHistory(String(targetUserId)));
    dispatch(fetchUserEducation(String(targetUserId)));
  }, [open, user, dispatch, activeTab, matcheeUser]);

  const formatYearRange = (start?: string, end?: string | null, currentlyWorking?: boolean) => {
    const startLabel = start ? String(start) : 'Not set';
    if (currentlyWorking) return `${startLabel} - Present`;
    const endLabel = end ? String(end) : 'Not set';
    return `${startLabel} - ${endLabel}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '600px', md: '900px' },
          maxHeight: '90vh',
          background: 'var(--Fill-Modal-background, #0F0F0F) !important',
          borderRadius: '8px',
          outline: '1px solid var(--Stroke-Modal-border, #444444)',
          outlineOffset: '-1px',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* ---------- HEADER ---------- */}
      <Box
        sx={{
          width: '100%',
          px: { xs: 2, md: 3 },
          py: { xs: 1.5, md: 1.5 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        {/* Tabs (when two profiles available) or plain title (single profile) */}
        {matcheeUser ? (
          <Box
            sx={{
              display: 'inline-flex',
              borderRadius: '6px',
              overflow: 'hidden',
              border: '1px solid #2A2A2A'
            }}
          >
            {(['candidate', 'matchee'] as const).map((tab) => {
              const label = tab === 'candidate' ? candidateLabel : matcheeLabel;
              const isActive = activeTab === tab;
              return (
                <Box
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  sx={{
                    px: 2,
                    py: 0.75,
                    fontSize: 13,
                    fontWeight: isActive ? 700 : 400,
                    color: isActive ? '#FFFFFF' : '#B6B6B6',
                    background: isActive ? '#F79420' : 'transparent',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background 0.15s, color 0.15s'
                  }}
                >
                  {label}
                </Box>
              );
            })}
          </Box>
        ) : (
          <Typography
            sx={{
              color: 'var(--Text-text-primary, #FDFDFD)',
              fontSize: 18,
              fontFamily: 'Inter !important',
              fontWeight: 700
            }}
          >
            {resolvedRoleLabel} Profile
          </Typography>
        )}

        {/* Close Icon */}
        <Box onClick={onClose} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}>
          <CloseOutlinedIcon style={{ color: 'white' }} />
        </Box>
      </Box>

      {/* ---------- BODY WITH SCROLL ---------- */}
      <Box
        sx={{
          // overflowY: 'auto',
          // maxHeight: 'calc(90vh - 64px)',
          p: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
      >
        {/* ----------- PROFILE HEADER CARD ----------- */}
        <Box
          sx={{
            width: '100%',
            pb: 3,
            borderBottom: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            flexWrap: { xs: 'wrap', md: 'nowrap' }
          }}
        >
          <ChatAvatarComponent
            image={profileImage}
            firstLetter={user?.name?.charAt(0) || user?.firstName?.charAt(0) || ''}
            width="100px"
            height="100px"
            type="noStatus"
            variant="rectangle"
          />

          {/* Name block */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start', // impede stretch das crianças
              gap: 1.5
            }}
          >
            <Typography
              sx={{
                color: 'var(--Text-text-primary, #FDFDFD)',
                fontSize: 18,
                fontWeight: 700
              }}
            >
              {name}
            </Typography>

            {hasValue(email) && (
              <Typography
                sx={{
                  color: 'var(--Text-text-secondary, #B6B6B6)',
                  fontSize: 16,
                  fontWeight: 500
                }}
              >
                {email}
              </Typography>
            )}

            {hasValue(roleCategory) && (
              <Box
                sx={{
                  px: 1.5,
                  py: '3px',
                  borderRadius: '20px',
                  outline: '1px solid var(--blue-500, #02A9FE)',
                  outlineOffset: '-1px',
                  display: 'inline-flex',
                  width: 'auto'
                }}
              >
                <Typography sx={{ color: '#FDFDFD', fontSize: 12 }}>{roleCategory}</Typography>
              </Box>
            )}
          </Box>

          {/* CTA Button */}
          <Box
            sx={{
              background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
              borderRadius: '6px',
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer'
            }}
            onClick={handleMessage}
          >
            <Typography
              sx={{
                color: '#FDFDFD',
                fontSize: 13,
                fontWeight: 600
              }}
            >
              Message
            </Typography>
          </Box>
        </Box>

        {/* ----------- DETAILS (every filled personal field) ----------- */}
        {hasDetails && (
          <CardWrapper title="Details">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {detailRows.map((r) => (
                <ProfileField key={r.label} label={r.label} value={r.value} />
              ))}
              {departmentNames.length > 0 && (
                <Box>
                  <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 600, mb: 1 }}>Departments</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {departmentNames.map((d) => (
                      <Tag key={d} label={d} />
                    ))}
                  </Box>
                </Box>
              )}
              {hobbies.length > 0 && (
                <Box>
                  <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 600, mb: 1 }}>
                    Hobbies and interests
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {hobbies.map((h) => (
                      <Tag key={h} label={h} />
                    ))}
                  </Box>
                </Box>
              )}
              {disability.length > 0 && (
                <Box>
                  <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 600, mb: 1 }}>Disability</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {disability.map((d) => (
                      <Tag key={d} label={d} />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </CardWrapper>
        )}

        {/* ----------- BIO (only when has content) ----------- */}
        {hasValue(bioText) && (
          <CardWrapper title="Bio">
            <Typography sx={{ color: '#B6B6B6', fontSize: 14, lineHeight: '20px' }}>{bioText}</Typography>
          </CardWrapper>
        )}

        {/* ----------- LEARNING OBJECTIVES / EXPERTISE ----------- */}
        {learningObjectives.length > 0 && (
          <CardWrapper
            title={
              objectivesTitleOverride || (resolvedRole === 'Mentor' ? 'Areas of Expertise' : 'Learning Objectives')
            }
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {learningObjectives.map((obj) => (
                <Tag key={obj} label={obj} />
              ))}
            </Box>
          </CardWrapper>
        )}

        {/* ----------- MY PROGRAMS ---------- */}
        {programs.length > 0 && (
          <CardWrapper title="My Programs">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {programs.map((p) => (
                <Tag key={p} label={p} />
              ))}
            </Box>
          </CardWrapper>
        )}

        {/* ----------- COMPANY (extended) ----------- */}
        {hasCompanyData && (
          <CardWrapper title="Company">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {hasValue(companyData.name) && <ProfileField label="Company name" value={companyData.name} />}
              {hasValue(companyData.location) && <ProfileField label="Location" value={companyData.location} />}
              {hasValue(companyData.mission) && <ProfileField label="Mission" value={companyData.mission} />}
              {hasValue(companyData.fundingStage) && (
                <ProfileField label="Funding stage" value={companyData.fundingStage} />
              )}
              {companyData.markets.length > 0 && (
                <ProfileField label="Markets" value={companyData.markets.join(', ')} />
              )}
              {hasValue(companyData.companySize) && (
                <ProfileField label="Company size" value={companyData.companySize} />
              )}
              {companyData.businessModel.length > 0 && (
                <ProfileField label="Business model" value={companyData.businessModel.join(', ')} />
              )}
              {hasValue(companyData.customers) && <ProfileField label="Customers" value={companyData.customers} />}
              {companyData.positions.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 600, mb: 1 }}>Positions</Typography>
                  {companyData.positions.map((pos: any, idx: number) => (
                    <Box key={pos?._id || idx} sx={{ mb: idx < companyData.positions.length - 1 ? 1.5 : 0 }}>
                      <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>
                        {toText(pos.companyTitle || pos.title, 'Not set')}
                      </Typography>
                      {hasValue(pos.responsibilities) && (
                        <Typography sx={{ color: '#868686', fontSize: 13 }}>
                          {Array.isArray(pos.responsibilities)
                            ? pos.responsibilities.join(', ')
                            : toText(pos.responsibilities, '')}
                        </Typography>
                      )}
                      {(hasValue(pos.startDate) || hasValue(pos.endDate)) && (
                        <Typography sx={{ color: '#868686', fontSize: 12 }}>
                          {toText(pos.startDate, '')} {pos.endDate ? `to ${toText(pos.endDate, '')}` : 'to Present'}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </CardWrapper>
        )}

        {/* ----------- SOCIALS ----------- */}
        {hasSocialsData && (
          <CardWrapper title="Socials">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {socialsData.map(({ label, url }) => (
                <Box
                  key={label}
                  component="a"
                  href={url.startsWith('http') ? url : `https://${url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    px: 1.5,
                    py: '6px',
                    borderRadius: '6px',
                    outline: '1px solid #444',
                    outlineOffset: '-1px',
                    color: '#F79420',
                    fontSize: 13,
                    fontWeight: 500,
                    textDecoration: 'none',
                    '&:hover': { opacity: 0.9 }
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>
          </CardWrapper>
        )}

        {/* ----------- TWO COLUMN CARDS RESPONSIVE ----------- */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3
          }}
        >
          {workHistoryList.length > 0 && (
            <CardWrapper title="Work experience">
              {workHistoryList.map((item: any, idx: number) => (
                <Box key={item?._id || `${item?.company_name}-${idx}`} sx={{ mb: idx ? 2 : 0 }}>
                  <WorkItem
                    title={toText(item?.title || item?.roleType?.[0] || item?.role, 'Role')}
                    company={toText(item?.company_name || item?.companyName, 'Not set')}
                    years={formatYearRange(item?.start_date, item?.end_date, item?.currentlyWorking)}
                    area={toText(item?.industry || companyInfo.markets?.[0], 'Not set')}
                    skills={toText(item?.roleType, 'Not set')}
                  />
                </Box>
              ))}
            </CardWrapper>
          )}

          {educationList.length > 0 && (
            <CardWrapper title="Education and skills">
              {educationList.map((item: any, idx: number) => (
                <Box key={item?._id || `${item?.university}-${idx}`} sx={{ mb: idx ? 2 : 0 }}>
                  <WorkItem
                    title={toText(item?.major || item?.degree, 'Degree')}
                    company={toText(item?.university || item?.school, 'Not set')}
                    years={item?.graduation_date ? String(item.graduation_date) : 'Not set'}
                    area={toText(item?.department || item?.school, 'Not set')}
                    skills={toText(item?.skills, 'Not set')}
                  />
                </Box>
              ))}
            </CardWrapper>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

/* ---------------------------------------------------------- */
/* ---------------------- SHARED COMPONENTS ------------------ */
/* ---------------------------------------------------------- */

const CardWrapper = ({ title, children }: any) => (
  <Box
    sx={{
      background: 'var(--Fill-Card-card-highlight, #1C1C1C)',
      borderRadius: 2,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}
  >
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: '1px solid #444',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <Typography
        sx={{
          flex: 1,
          color: '#FDFDFD',
          fontSize: 16,
          fontWeight: 700
        }}
      >
        {title}
      </Typography>
    </Box>

    {/* Scroll interno se crescer demais */}
    <Box
      sx={{
        maxHeight: '420px',
        overflowY: 'auto',
        px: 2.5,
        py: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      {children}
    </Box>
  </Box>
);

const WorkItem = ({ title, company, years, area, skills }: any) => (
  <Box sx={{ display: 'flex', gap: 2 }}>
    <WorkOutlineOutlinedIcon style={{ color: 'white' }} />

    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ color: '#868686', fontSize: 14 }}>{company}</Typography>
      <Typography sx={{ color: '#868686', fontSize: 14 }}>{years}</Typography>
      <Typography sx={{ color: '#FDFDFD', fontSize: 14 }}>{area}</Typography>
      <Typography sx={{ color: '#FDFDFD', fontSize: 14 }}>{skills}</Typography>
    </Box>
  </Box>
);

const Tag = ({ label }: any) => (
  <Box
    sx={{
      px: 1.5,
      py: '3px',
      borderRadius: '20px',
      outline: '1px solid #444',
      outlineOffset: '-1px'
    }}
  >
    <Typography sx={{ color: '#FDFDFD', fontSize: 12 }}>{label}</Typography>
  </Box>
);

const ProfileField = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography sx={{ color: '#868686', fontSize: 12, fontWeight: 500 }}>{label}</Typography>
    <Typography sx={{ color: '#FDFDFD', fontSize: 14 }}>{value}</Typography>
  </Box>
);

export default MenteeProfileModal;
